import { shallowEqual } from 'react-redux';
import {
	setNavButtonsTexts,
	setCustomTitles,
	setTemporaryView,
	setDefaultDate
} from 'store/actions';
import { CUSTOM_VIEWS, VIEW_PLUGIN_NAMES } from 'helpers/constants';
import { getViewPluginName, hasAnyProperty, getDefaultDate, isDefined } from 'helpers/commons';

import { updateLocalize } from './localize';
import { updateViews } from './views';
import { processCustomTitles, processNavButtonsTexts, updateHeader } from './header';
import { updateDesign } from './design';
import { setShowButtonText, setWeatherForecastCity, setWeatherForecast } from '../../store/actions';
import { getWeatherForecast } from 'api/weather';

export const updateSettingsParams = (current, updates, dispatch) => {
	// Processing header buttons
	const processedNavButtonsTexts = processNavButtonsTexts(current.headerButtonsTexts, updates);

	const showMoreBtnUpdateTxt =
		updates.showMoreButtonText ?? current.singleEventOptions.showMoreButtonText;
	const weatherForecastCity =
		updates.weatherForecastCity ?? current.monthSpecifics.weatherForecastCity;
	const temperatureUnit = updates.temperatureUnit ?? current.monthSpecifics.temperatureUnit;

	if (showMoreBtnUpdateTxt !== current.singleEventOptions.showMoreButtonText) {
		current.singleEventOptions.showMoreButtonText = showMoreBtnUpdateTxt;
		dispatch(setShowButtonText(current.singleEventOptions.showMoreButtonText));
	}

	if (
		weatherForecastCity &&
		(weatherForecastCity !== current.monthSpecifics.weatherForecastCity ||
			updates.temperatureUnit !== current.monthSpecifics.temperatureUnit)
	) {
		current.monthSpecifics.weatherForecastCity = weatherForecastCity;
		current.monthSpecifics.temperatureUnit = temperatureUnit;
		dispatch(setWeatherForecastCity(current.monthSpecifics.weatherForecastCity));
		// Get updated weather forecast for days and append to update object
		getWeatherForecast(
			current.monthSpecifics.weatherForecastCity,
			current.monthSpecifics.temperatureUnit
		).then(forecast => {
			dispatch(setWeatherForecast(forecast));
		});
	}

	if (!shallowEqual(processedNavButtonsTexts, current.headerButtonsTexts)) {
		current.headerButtonsTexts = processedNavButtonsTexts;
		dispatch(setNavButtonsTexts(processedNavButtonsTexts));
	}

	if (updates.customDate) {
		current.localize.defaultDate = getDefaultDate(11, updates.customDate);
		dispatch(setDefaultDate(current.localize.defaultDate));
	}

	// Processing header titles
	const processedCustomTitles = processCustomTitles(current.headerTitlesTexts, updates);
	const titles = {};
	CUSTOM_VIEWS.forEach(view => (titles[view] = current.headerTitlesTexts[view]));
	if (!shallowEqual(titles, processedCustomTitles)) {
		current.headerTitlesTexts = processedCustomTitles;
		dispatch(setCustomTitles(processedCustomTitles));
	}

	// Processing temporary view
	if (!updates.hasOwnProperty('toView')) return;
	const processedTemporaryView = getViewPluginName({
		viewIndex: updates.toView,
		showHours: current.weekDaySpecifics.showHours,
		viewNames: VIEW_PLUGIN_NAMES,
		listViewType: current.listSpecifics.type,
		yearViewStyle: current.yearlySpecifics.style
	});
	current.temporaryView = processedTemporaryView;
	dispatch(setTemporaryView(processedTemporaryView));
};

export const updateInitialSettingsParams = (current, updates) => {
	const processedNavButtonsTexts = processNavButtonsTexts(current.headerButtonsTexts, updates);
	current.headerButtonsTexts = processedNavButtonsTexts;
	if (!updates) return;
	if (isDefined(updates.headerText)) current.headerTitlesTexts.agenda = updates.headerText;
	if (isDefined(updates.gridText)) current.headerTitlesTexts.card = updates.gridText;
	if (isDefined(updates.mapText)) current.headerTitlesTexts.map = updates.mapText;
	if (isDefined(updates.timelineText)) current.headerTitlesTexts.timeline = updates.timelineText;
	if (isDefined(updates.crossText)) current.headerTitlesTexts.cross = updates.crossText;
	if (isDefined(updates.mobileNoEventsText))
		current.singleEventOptions.mobileNoEventsText = updates.mobileNoEventsText;
	if (isDefined(updates.showMoreButtonText))
		current.singleEventOptions.showMoreButtonText = updates.showMoreButtonText;
	if (isDefined(updates.weatherForecastCity))
		current.monthSpecifics.weatherForecastCity = updates.weatherForecastCity;

	if (updates.customDate) current.localize.defaultDate = getDefaultDate(11, updates.customDate);
	if (isDefined(updates.customViewText))
		current.headerTitlesTexts.customView = updates.customViewText;
};

export const updateStyleParams = async (
	current,
	updates,
	platformData,
	dispatch,
	settingsParams
) => {
	if (!hasAnyProperty(updates)) return;

	const {
		colors,
		booleans,
		numbers,
		fonts: { agendaDateFontStyle = {} }
	} = updates;

	// Creating flags to optimize changes count inside of the functions called below
	let updateNeeded = {
		booleans: hasAnyProperty(booleans),
		numbers: hasAnyProperty(numbers),
		colors: hasAnyProperty(colors),
		fonts: hasAnyProperty(agendaDateFontStyle)
	};

	await updateLocalize(current, updates, updateNeeded, settingsParams);
	updateViews(current, updates, dispatch, updateNeeded);
	updateHeader(current, updates, updateNeeded);
	updateDesign(current, updates, platformData, updateNeeded);
};
